'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    MdAdd,
    MdCheckCircle,
    MdDelete,
    MdErrorOutline,
    MdHistory,
    MdInfoOutline,
    MdPublish,
    MdRefresh,
    MdSave,
    MdSync,
} from 'react-icons/md'
import { FaWhatsapp } from 'react-icons/fa'
import { toast } from 'react-toastify'

import ConfirmModal from '@/components/confirm-modal'
import {
    WHATSAPP_TEMPLATE_APPROVAL_STATUS_COLORS,
    WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS,
    WhatsappTemplateApprovalStatus,
    WhatsappTemplateCategory,
} from '@/lib/api/messaging/whatsapp-templates-api'
import {
    createSystemTemplateDraft,
    discardSystemTemplateDraft,
    getSystemTemplateScope,
    getSystemTemplatesOverview,
    propagateSystemTemplates,
    publishSystemTemplateDraft,
    SystemTemplateOverviewItem,
    SystemTemplateScopeDetail,
    SystemTemplateVersion,
    syncSystemTemplatesForOrganization,
    updateSystemTemplateDraft,
} from '@/lib/api/master/master-whatsapp-system-templates-api'

const CATEGORY_LABELS: Record<WhatsappTemplateCategory, string> = {
    UTILITY: 'Utilidade',
    MARKETING: 'Marketing',
    AUTHENTICATION: 'Autenticação',
}

const VERSION_STATUS_LABELS: Record<SystemTemplateVersion['status'], string> = {
    DRAFT: 'Em edição',
    PUBLISHED: 'Vigente',
    RETIRED: 'Substituída',
}

const formatDateTime = (iso: string | null): string =>
    iso
        ? new Date(iso).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—'

const renderWithExamples = (body: string, version: Pick<SystemTemplateVersion, 'variables'>) =>
    body.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n: string) => {
        const example = version.variables.find(v => v.index === Number(n))?.example
        return example?.trim() ? example : match
    })

interface DraftForm {
    friendlyName: string
    category: WhatsappTemplateCategory
    headerText: string
    bodyText: string
    footerText: string
    examples: Record<number, string>
}

const formFromVersion = (version: SystemTemplateVersion): DraftForm => ({
    friendlyName: version.friendlyName,
    category: version.category,
    headerText: version.headerText ?? '',
    bodyText: version.bodyText,
    footerText: version.footerText ?? '',
    examples: Object.fromEntries(version.variables.map(v => [v.index, v.example])),
})

/**
 * Avisos imediatos enquanto o operador digita. A validação que vale é a do
 * backend, na publicação; aqui só se antecipa o que já se sabe que a Meta ou o
 * envio recusariam.
 */
function draftWarnings(form: DraftForm, variableCount: number): string[] {
    const warnings: string[] = []
    const used = new Set(
        [...form.bodyText.matchAll(/\{\{\s*([^}]*?)\s*\}\}/g)].map(m => m[1]),
    )
    const expected = Array.from({ length: variableCount }, (_, i) => String(i + 1))
    const missing = expected.filter(n => !used.has(n))
    const extra = [...used].filter(token => !expected.includes(token))
    if (missing.length > 0) {
        warnings.push(`Faltam no corpo: ${missing.map(n => `{{${n}}}`).join(', ')}.`)
    }
    if (extra.length > 0) {
        warnings.push(`Variáveis que o envio não preenche: ${extra.map(n => `{{${n}}}`).join(', ')}.`)
    }
    if (/\{\{[^}]+\}\}[^\p{L}\p{N}]*$/u.test(form.bodyText)) {
        warnings.push('O corpo precisa terminar com texto fixo, e não com uma variável.')
    }
    if (/\{\{/.test(form.headerText) || /\{\{/.test(form.footerText)) {
        warnings.push('Cabeçalho e rodapé não podem ter variáveis.')
    }
    return warnings
}

export default function WhatsappSystemTemplatesPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedScope = searchParams.get('scope')

    const [overview, setOverview] = useState<SystemTemplateOverviewItem[]>([])
    const [loadingOverview, setLoadingOverview] = useState(true)
    const [detail, setDetail] = useState<SystemTemplateScopeDetail | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)

    const [form, setForm] = useState<DraftForm | null>(null)
    const [saving, setSaving] = useState(false)
    const [creating, setCreating] = useState(false)
    const [publishing, setPublishing] = useState(false)
    const [discarding, setDiscarding] = useState(false)
    const [propagating, setPropagating] = useState(false)
    const [syncingOrg, setSyncingOrg] = useState<number | null>(null)
    const [confirm, setConfirm] = useState<'publish' | 'discard' | null>(null)
    const bodyRef = useRef<HTMLTextAreaElement>(null)

    const loadOverview = useCallback(async () => {
        setLoadingOverview(true)
        const res = await getSystemTemplatesOverview()
        setLoadingOverview(false)
        if (res.error) {
            toast.error(res.error)
            return
        }
        setOverview(res.result ?? [])
    }, [])

    const loadDetail = useCallback(async (scope: string) => {
        setLoadingDetail(true)
        const res = await getSystemTemplateScope(scope)
        setLoadingDetail(false)
        if (res.error) {
            toast.error(res.error)
            return
        }
        setDetail(res.result)
        const draft = res.result?.versions.find(v => v.status === 'DRAFT')
        setForm(draft ? formFromVersion(draft) : null)
    }, [])

    useEffect(() => {
        loadOverview()
    }, [loadOverview])

    useEffect(() => {
        if (!selectedScope && overview.length > 0) {
            setSearchParams({ scope: overview[0].scope }, { replace: true })
        }
    }, [selectedScope, overview, setSearchParams])

    useEffect(() => {
        if (!selectedScope) return
        setDetail(null)
        setForm(null)
        loadDetail(selectedScope)
    }, [selectedScope, loadDetail])

    const current = overview.find(item => item.scope === selectedScope) ?? null
    const versions = detail?.versions ?? []
    const published = versions.find(v => v.status === 'PUBLISHED') ?? null
    const draft = versions.find(v => v.status === 'DRAFT') ?? null
    const contractVariables = current?.variables ?? []
    const warnings = useMemo(
        () => (form ? draftWarnings(form, contractVariables.length) : []),
        [form, contractVariables.length],
    )

    const dirty = useMemo(() => {
        if (!form || !draft) return false
        return JSON.stringify(form) !== JSON.stringify(formFromVersion(draft))
    }, [form, draft])

    async function refreshAll() {
        await loadOverview()
        if (selectedScope) await loadDetail(selectedScope)
    }

    async function handleCreateDraft() {
        if (!selectedScope) return
        setCreating(true)
        const res = await createSystemTemplateDraft(selectedScope)
        setCreating(false)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success(`Versão ${res.result.version} aberta para edição.`)
        await refreshAll()
    }

    async function saveDraft(): Promise<boolean> {
        if (!form || !draft) return false
        setSaving(true)
        const res = await updateSystemTemplateDraft(draft.id, {
            friendlyName: form.friendlyName,
            category: form.category,
            headerText: form.headerText.trim() || null,
            bodyText: form.bodyText,
            footerText: form.footerText.trim() || null,
            variables: contractVariables.map(v => ({
                index: v.index,
                example: form.examples[v.index] ?? '',
            })),
        })
        setSaving(false)
        if (res.error) {
            toast.error(res.error)
            return false
        }
        await refreshAll()
        return true
    }

    async function handleSave() {
        if (await saveDraft()) toast.success('Rascunho salvo.')
    }

    async function handlePublish() {
        if (!draft) return
        setPublishing(true)
        // O que está na tela é o que se publica: salva antes, se houver alteração.
        if (dirty && !(await saveDraft())) {
            setPublishing(false)
            setConfirm(null)
            return
        }
        const res = await publishSystemTemplateDraft(draft.id)
        setPublishing(false)
        setConfirm(null)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success(
            `Versão ${res.result.version} publicada. A propagação para as organizações foi iniciada.`,
        )
        await refreshAll()
    }

    async function handleDiscard() {
        if (!draft) return
        setDiscarding(true)
        const res = await discardSystemTemplateDraft(draft.id)
        setDiscarding(false)
        setConfirm(null)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Versão em edição descartada.')
        await refreshAll()
    }

    async function handlePropagate() {
        setPropagating(true)
        const res = await propagateSystemTemplates()
        setPropagating(false)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Propagação enfileirada. Atualize a tela em alguns instantes.')
    }

    async function handleSyncOrganization(organizationId: number) {
        setSyncingOrg(organizationId)
        const res = await syncSystemTemplatesForOrganization(organizationId)
        setSyncingOrg(null)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Status sincronizado com a Meta.')
        await refreshAll()
    }

    function insertVariable(index: number) {
        if (!form) return
        const token = `{{${index}}}`
        const el = bodyRef.current
        const start = el?.selectionStart ?? form.bodyText.length
        const end = el?.selectionEnd ?? form.bodyText.length
        const bodyText = form.bodyText.slice(0, start) + token + form.bodyText.slice(end)
        setForm({ ...form, bodyText })
        requestAnimationFrame(() => {
            el?.focus()
            el?.setSelectionRange(start + token.length, start + token.length)
        })
    }

    const copySummary = (item: SystemTemplateOverviewItem) => {
        const byStatus = new Map<WhatsappTemplateApprovalStatus, number>()
        for (const copy of item.copies) {
            byStatus.set(copy.approvalStatus, (byStatus.get(copy.approvalStatus) ?? 0) + copy.count)
        }
        return [...byStatus.entries()]
    }

    const organizations = detail?.organizations ?? []

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div className="max-w-3xl">
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <FaWhatsapp /> Templates do sistema
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Mensagens que a plataforma envia sozinha (link de pagamento, lembrete,
                        confirmação de agendamento). Só este painel vê e edita esses templates; a
                        versão publicada é levada a todas as organizações pela task, e as
                        organizações não conseguem alterá-la.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-ghost"
                        onClick={refreshAll}
                        title="Recarregar"
                    >
                        <MdRefresh className="text-base" />
                    </button>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={handlePropagate}
                        disabled={propagating}
                    >
                        {propagating ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdSync className="text-base" />
                        )}
                        Propagar agora
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
                {/* Escopos */}
                <div className="card bg-base-100 border border-base-300">
                    <div className="card-body p-2">
                        {loadingOverview && overview.length === 0 && (
                            <div className="p-4 flex justify-center">
                                <span className="loading loading-spinner" />
                            </div>
                        )}
                        <ul className="flex flex-col gap-1">
                            {overview.map(item => {
                                const active = item.scope === selectedScope
                                return (
                                    <li key={item.scope}>
                                        <button
                                            type="button"
                                            onClick={() => setSearchParams({ scope: item.scope })}
                                            className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                                                active
                                                    ? 'bg-primary/10 border border-primary/30'
                                                    : 'hover:bg-base-200 border border-transparent'
                                            }`}
                                        >
                                            <div className="font-medium text-sm leading-snug">
                                                {item.label}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                                {item.published ? (
                                                    <span className="badge badge-sm badge-outline">
                                                        v{item.published.version}
                                                    </span>
                                                ) : (
                                                    !item.draft && (
                                                        <span className="badge badge-sm badge-warning">
                                                            sem template
                                                        </span>
                                                    )
                                                )}
                                                {item.draft && (
                                                    <span className="badge badge-sm badge-info">
                                                        v{item.draft.version} em edição
                                                    </span>
                                                )}
                                                {copySummary(item).map(([status, count]) => (
                                                    <span
                                                        key={status}
                                                        className={`badge badge-sm ${WHATSAPP_TEMPLATE_APPROVAL_STATUS_COLORS[status]}`}
                                                        title={WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS[status]}
                                                    >
                                                        {count} {WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS[status].toLowerCase()}
                                                    </span>
                                                ))}
                                            </div>
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                </div>

                {/* Detalhe */}
                <div className="flex flex-col gap-4 min-w-0">
                    {loadingDetail && !detail && (
                        <div className="card bg-base-100 border border-base-300">
                            <div className="card-body items-center">
                                <span className="loading loading-spinner" />
                            </div>
                        </div>
                    )}

                    {detail && current && (
                        <>
                            <div className="card bg-base-100 border border-base-300">
                                <div className="card-body gap-4">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div>
                                            <h2 className="text-lg font-semibold">
                                                {current.label}
                                            </h2>
                                            <div className="text-xs text-base-content/60 mt-1 font-mono">
                                                {current.scope}
                                            </div>
                                            {current.requiresSecondarySegment === 'INTERMEDIATION' && (
                                                <div className="text-xs text-base-content/60 mt-1">
                                                    Só em organizações com intermediação.
                                                </div>
                                            )}
                                        </div>
                                        {!draft && (
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={handleCreateDraft}
                                                disabled={creating}
                                            >
                                                {creating ? (
                                                    <span className="loading loading-spinner loading-xs" />
                                                ) : (
                                                    <MdAdd className="text-base" />
                                                )}
                                                {published ? 'Nova versão' : 'Criar template'}
                                            </button>
                                        )}
                                    </div>

                                    {!published && !draft && (
                                        <div className="alert alert-warning text-sm py-3">
                                            <MdErrorOutline className="text-xl shrink-0" />
                                            <span>
                                                Esta notificação ainda não tem template. Na API oficial ela
                                                não é enviada; no WhatsApp Web sai com o texto padrão do
                                                sistema, quando existe. Crie o template, publique e a task o
                                                leva a todas as organizações.
                                            </span>
                                        </div>
                                    )}

                                    <div>
                                        <div className="text-xs font-medium text-base-content/60 mb-1.5">
                                            Variáveis (definidas pelo envio, não mudam)
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {contractVariables.map(v => (
                                                <span key={v.index} className="badge badge-ghost gap-1">
                                                    <span className="font-mono">{`{{${v.index}}}`}</span>
                                                    {v.label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {published && (
                                        <div className="rounded-lg border border-base-300 p-4">
                                            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="badge badge-success">
                                                        v{published.version} vigente
                                                    </span>
                                                    <span className="text-sm font-medium">
                                                        {published.friendlyName}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-base-content/60">
                                                    {CATEGORY_LABELS[published.category]} · publicada em{' '}
                                                    {formatDateTime(published.publishedAt)}
                                                </span>
                                            </div>
                                            <MessagePreview version={published} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {draft && form && (
                                <div className="card bg-base-100 border border-info/40">
                                    <div className="card-body gap-5">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <h3 className="font-semibold flex items-center gap-2">
                                                <span className="badge badge-info">
                                                    v{draft.version} em edição
                                                </span>
                                                <span className="text-xs font-normal text-base-content/60 font-mono">
                                                    {draft.name}
                                                </span>
                                            </h3>
                                        </div>

                                        <div className="alert alert-info text-sm py-3">
                                            <MdInfoOutline className="text-xl shrink-0" />
                                            <span>
                                                Ao publicar, a versão nova é criada em cada organização e
                                                enviada à Meta. A versão anterior continua sendo usada até
                                                a nova ser aprovada naquela organização; só então é apagada.
                                                Se a Meta recusar, a anterior segue valendo.
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1.5 min-w-0">
                                                <label className="flex items-baseline justify-between gap-3" htmlFor="st-friendly">
                                                    <span className="text-sm font-medium">Nome de exibição</span>
                                                </label>
                                                <input
                                                    id="st-friendly"
                                                    className="input input-bordered w-full"
                                                    value={form.friendlyName}
                                                    maxLength={120}
                                                    onChange={e => setForm({ ...form, friendlyName: e.target.value })}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1.5 min-w-0">
                                                <label className="flex items-baseline justify-between gap-3" htmlFor="st-category">
                                                    <span className="text-sm font-medium">Categoria na Meta</span>
                                                </label>
                                                <select
                                                    id="st-category"
                                                    className="select select-bordered w-full"
                                                    value={form.category}
                                                    onChange={e =>
                                                        setForm({
                                                            ...form,
                                                            category: e.target.value as WhatsappTemplateCategory,
                                                        })
                                                    }
                                                >
                                                    {(Object.keys(CATEGORY_LABELS) as WhatsappTemplateCategory[]).map(c => (
                                                        <option key={c} value={c}>
                                                            {CATEGORY_LABELS[c]}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5 min-w-0">
                                            <label className="flex items-baseline justify-between gap-3" htmlFor="st-header">
                                                <span className="text-sm font-medium">Cabeçalho</span>
                                                <span className="text-xs text-base-content/50">
                                                    Opcional, até 60 caracteres
                                                </span>
                                            </label>
                                            <input
                                                id="st-header"
                                                className="input input-bordered w-full"
                                                value={form.headerText}
                                                maxLength={60}
                                                onChange={e => setForm({ ...form, headerText: e.target.value })}
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5 min-w-0">
                                            <label className="flex items-baseline justify-between gap-3" htmlFor="st-body">
                                                <span className="text-sm font-medium">Corpo da mensagem</span>
                                                <span className="text-xs text-base-content/50">
                                                    {form.bodyText.length}/1024
                                                </span>
                                            </label>
                                            <div className="flex flex-wrap gap-1.5">
                                                {contractVariables.map(v => (
                                                    <button
                                                        key={v.index}
                                                        type="button"
                                                        className="btn btn-xs btn-outline normal-case"
                                                        onClick={() => insertVariable(v.index)}
                                                        title="Inserir na posição do cursor"
                                                    >
                                                        <span className="font-mono">{`{{${v.index}}}`}</span>
                                                        {v.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <textarea
                                                id="st-body"
                                                ref={bodyRef}
                                                className="textarea textarea-bordered w-full min-h-48 font-mono text-sm leading-relaxed"
                                                value={form.bodyText}
                                                maxLength={1024}
                                                onChange={e => setForm({ ...form, bodyText: e.target.value })}
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5 min-w-0">
                                            <label className="flex items-baseline justify-between gap-3" htmlFor="st-footer">
                                                <span className="text-sm font-medium">Rodapé</span>
                                                <span className="text-xs text-base-content/50">
                                                    Opcional, até 60 caracteres
                                                </span>
                                            </label>
                                            <input
                                                id="st-footer"
                                                className="input input-bordered w-full"
                                                value={form.footerText}
                                                maxLength={60}
                                                onChange={e => setForm({ ...form, footerText: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <div className="text-sm font-medium mb-1">Exemplos para a revisão da Meta</div>
                                            <p className="text-xs text-base-content/60 mb-3">
                                                O revisor lê a mensagem com estes valores no lugar das
                                                variáveis. Use valores realistas: exemplo genérico costuma
                                                ser recusado.
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {contractVariables.map(v => (
                                                    <div key={v.index} className="flex flex-col gap-1.5 min-w-0">
                                                        <label className="flex items-baseline justify-between gap-3" htmlFor={`st-example-${v.index}`}>
                                                            <span className="text-sm text-base-content/80">
                                                                <span className="font-mono">{`{{${v.index}}}`}</span>{' '}
                                                                {v.label}
                                                            </span>
                                                        </label>
                                                        <input
                                                            id={`st-example-${v.index}`}
                                                            className="input input-bordered input-sm w-full"
                                                            value={form.examples[v.index] ?? ''}
                                                            maxLength={200}
                                                            onChange={e =>
                                                                setForm({
                                                                    ...form,
                                                                    examples: { ...form.examples, [v.index]: e.target.value },
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="text-sm font-medium mb-2">Pré-visualização</div>
                                            <MessagePreview
                                                version={{
                                                    headerText: form.headerText || null,
                                                    bodyText: form.bodyText,
                                                    footerText: form.footerText || null,
                                                    variables: contractVariables.map(v => ({
                                                        index: v.index,
                                                        name: v.name,
                                                        example: form.examples[v.index] ?? '',
                                                    })),
                                                }}
                                            />
                                        </div>

                                        {warnings.length > 0 && (
                                            <div className="alert alert-warning text-sm py-3 items-start">
                                                <MdErrorOutline className="text-xl shrink-0" />
                                                <ul className="list-disc pl-4">
                                                    {warnings.map(w => (
                                                        <li key={w}>{w}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-base-300">
                                            <button
                                                className="btn btn-sm btn-ghost text-error"
                                                onClick={() => setConfirm('discard')}
                                                disabled={discarding || publishing}
                                            >
                                                <MdDelete className="text-base" /> Descartar
                                            </button>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline"
                                                    onClick={handleSave}
                                                    disabled={!dirty || saving || publishing}
                                                >
                                                    {saving ? (
                                                        <span className="loading loading-spinner loading-xs" />
                                                    ) : (
                                                        <MdSave className="text-base" />
                                                    )}
                                                    Salvar rascunho
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => setConfirm('publish')}
                                                    disabled={warnings.length > 0 || publishing || saving}
                                                >
                                                    <MdPublish className="text-base" /> Publicar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="card bg-base-100 border border-base-300">
                                <div className="card-body gap-3">
                                    <h3 className="font-semibold">Situação nas organizações</h3>
                                    {organizations.length === 0 ? (
                                        <p className="text-sm text-base-content/60">
                                            Nenhuma organização tem este template ainda. A task cria as
                                            cópias na próxima passada, ou use "Propagar agora".
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Organização</th>
                                                        <th>Versão</th>
                                                        <th>Status</th>
                                                        <th>Motivo</th>
                                                        <th>Sincronizado</th>
                                                        <th />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {organizations.map(row => (
                                                        <tr key={row.templateId}>
                                                            <td>
                                                                <div className="font-medium">
                                                                    {row.organizationName ?? '—'}
                                                                </div>
                                                                <div className="text-xs text-base-content/60">
                                                                    #{row.organizationId}
                                                                    {row.hasWhatsappAccount
                                                                        ? ' · API oficial'
                                                                        : ' · sem conta da Meta'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-sm badge-outline">
                                                                    v{row.version ?? '?'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`badge badge-sm ${WHATSAPP_TEMPLATE_APPROVAL_STATUS_COLORS[row.approvalStatus]}`}
                                                                >
                                                                    {WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS[row.approvalStatus]}
                                                                </span>
                                                            </td>
                                                            <td className="max-w-xs">
                                                                <span className="text-xs text-base-content/70 break-words">
                                                                    {row.approvalRejectionReason ?? '—'}
                                                                </span>
                                                            </td>
                                                            <td className="text-xs whitespace-nowrap">
                                                                {formatDateTime(row.lastSyncedAt)}
                                                            </td>
                                                            <td className="text-right">
                                                                {row.hasWhatsappAccount && row.organizationId && (
                                                                    <button
                                                                        className="btn btn-xs btn-ghost"
                                                                        onClick={() => handleSyncOrganization(row.organizationId!)}
                                                                        disabled={syncingOrg === row.organizationId}
                                                                        title="Buscar o status na Meta"
                                                                    >
                                                                        {syncingOrg === row.organizationId ? (
                                                                            <span className="loading loading-spinner loading-xs" />
                                                                        ) : (
                                                                            <MdSync className="text-base" />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {versions.length > 1 && (
                                <div className="card bg-base-100 border border-base-300">
                                    <div className="card-body gap-3">
                                        <h3 className="font-semibold flex items-center gap-2">
                                            <MdHistory /> Versões
                                        </h3>
                                        <ul className="divide-y divide-base-300">
                                            {versions.map(v => (
                                                <li key={v.id} className="py-2 flex items-center justify-between gap-3 flex-wrap text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <span className="badge badge-sm badge-outline">v{v.version}</span>
                                                        <span>{v.friendlyName}</span>
                                                        <span className="text-xs text-base-content/60 font-mono">{v.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-base-content/60">
                                                        {v.status === 'PUBLISHED' && <MdCheckCircle className="text-success" />}
                                                        {VERSION_STATUS_LABELS[v.status]}
                                                        {v.publishedAt && ` · publicada ${formatDateTime(v.publishedAt)}`}
                                                        {v.retiredAt && ` · substituída ${formatDateTime(v.retiredAt)}`}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={confirm === 'publish'}
                onClose={() => setConfirm(null)}
                onConfirm={handlePublish}
                loading={publishing}
                title={`Publicar a versão ${draft?.version ?? ''}?`}
                message="A versão nova será criada em todas as organizações e enviada à Meta. A anterior continua valendo em cada organização até a nova ser aprovada ali. Depois de publicada, a versão não pode mais ser editada."
                confirmButtonText="Publicar"
                confirmButtonClass="btn btn-primary"
            />
            <ConfirmModal
                isOpen={confirm === 'discard'}
                onClose={() => setConfirm(null)}
                onConfirm={handleDiscard}
                loading={discarding}
                title="Descartar a versão em edição?"
                message="O rascunho é apagado. Nenhuma organização é afetada: ele ainda não foi publicado."
                confirmButtonText="Descartar"
            />
        </div>
    )
}

function MessagePreview({
    version,
}: {
    version: Pick<SystemTemplateVersion, 'headerText' | 'bodyText' | 'footerText' | 'variables'>
}) {
    return (
        <div className="rounded-xl bg-base-200 p-4">
            <div className="max-w-md rounded-lg bg-base-100 shadow-sm px-3 py-2.5 text-sm">
                {version.headerText && (
                    <div className="font-semibold mb-1.5">{version.headerText}</div>
                )}
                <div className="whitespace-pre-wrap break-words">
                    {renderWithExamples(version.bodyText, version)}
                </div>
                {version.footerText && (
                    <div className="text-xs text-base-content/50 mt-2">{version.footerText}</div>
                )}
            </div>
        </div>
    )
}
